import { useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { DayBriefing } from '@/services/dayBriefing';
import {
  cancelDayBriefingNotification,
  scheduleDayBriefingNotification,
} from '@/services/notifications';
import { shouldSendNotification } from '@/services/notificationGate';
import { checkAndMark } from '@/services/notificationDedupe';
import { toLocalDateKey } from '@/utils/dateKeys';

const STORAGE_KEY = '@tripflow_scheduled_day_briefings';
const DAY_BRIEFING_COOLDOWN_MS = 20 * 60 * 60 * 1000;
const SYNC_COOLDOWN_MS = 15_000;

type StoredDayBriefing = {
  key: string;
  notificationId: string;
  tripId: string;
  contentDigest: string;
};

const tripSyncQueue = new Map<string, Promise<void>>();
const recentSyncByTrip = new Map<string, { digest: string; at: number }>();

const enqueueTripSync = (tripId: string, task: () => Promise<void>) => {
  const prev = tripSyncQueue.get(tripId) ?? Promise.resolve();
  const next = prev
    .catch(() => {
      // Keep queue alive on upstream failures.
    })
    .then(task)
    .finally(() => {
      if (tripSyncQueue.get(tripId) === next) {
        tripSyncQueue.delete(tripId);
      }
    });
  tripSyncQueue.set(tripId, next);
  return next;
};

export const nextMorningTrigger = (): Date => {
  const trigger = new Date();
  trigger.setHours(8, 0, 0, 0);
  if (trigger.getTime() <= Date.now() + 60 * 1000) {
    trigger.setDate(trigger.getDate() + 1);
  }
  return trigger;
};

export const buildDayBriefingScheduleKey = (tripId: string, trigger: Date): string =>
  `${tripId}:${toLocalDateKey(trigger)}`;

export const buildDayBriefingDedupeKey = (tripId: string, trigger: Date): string =>
  `day-briefing:${buildDayBriefingScheduleKey(tripId, trigger)}`;

export const buildDayBriefingContentDigest = (briefing: DayBriefing): string =>
  briefing.items.map((item) => item.id).join('|');

const loadStored = async (): Promise<StoredDayBriefing[]> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StoredDayBriefing[]) : [];
  } catch {
    return [];
  }
};

const persistStored = async (entries: StoredDayBriefing[]): Promise<void> => {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Best-effort persistence.
  }
};

export const clearStoredDayBriefings = async (): Promise<void> => {
  try {
    const stored = await loadStored();
    await Promise.all(stored.map((entry) => cancelDayBriefingNotification(entry.notificationId)));
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort cleanup for logout/account switches.
  }
};

export function useDayBriefingNotifications(briefing: DayBriefing | null | undefined): void {
  const syncDigest = useMemo(() => {
    if (!briefing || briefing.items.length === 0) return '';
    const trigger = nextMorningTrigger();
    return [
      buildDayBriefingScheduleKey(briefing.tripId, trigger),
      buildDayBriefingContentDigest(briefing),
      briefing.dayContext.state,
    ].join('|');
  }, [briefing]);

  useEffect(() => {
    if (Platform.OS === 'web' || !briefing || briefing.dayContext.state !== 'active') {
      return;
    }
    if (briefing.items.length === 0 || !syncDigest) {
      return;
    }

    let cancelled = false;

    void enqueueTripSync(briefing.tripId, async () => {
      if (cancelled) return;

      const lastSync = recentSyncByTrip.get(briefing.tripId);
      if (lastSync && lastSync.digest === syncDigest && Date.now() - lastSync.at < SYNC_COOLDOWN_MS) {
        return;
      }

      const trigger = nextMorningTrigger();
      const scheduleKey = buildDayBriefingScheduleKey(briefing.tripId, trigger);
      const contentDigest = buildDayBriefingContentDigest(briefing);
      const dedupeKey = buildDayBriefingDedupeKey(briefing.tripId, trigger);

      const stored = await loadStored();
      const existing = stored.find((entry) => entry.key === scheduleKey);
      if (existing?.contentDigest === contentDigest) {
        recentSyncByTrip.set(briefing.tripId, { digest: syncDigest, at: Date.now() });
        return;
      }

      if (!existing) {
        const gate = await shouldSendNotification({
          category: 'itinerary',
          channel: 'push',
          tripId: briefing.tripId,
          priority: briefing.items.some((item) => item.priority === 'high') ? 'high' : 'medium',
          dedupeKey,
          cooldownMs: DAY_BRIEFING_COOLDOWN_MS,
        });
        if (!gate.allowed || cancelled) {
          return;
        }
      }

      if (existing?.notificationId) {
        await cancelDayBriefingNotification(existing.notificationId);
      }

      const notificationId = await scheduleDayBriefingNotification(briefing, trigger, {
        skipPermissionCheck: true,
      });
      if (!notificationId || cancelled) {
        return;
      }

      await checkAndMark(dedupeKey, DAY_BRIEFING_COOLDOWN_MS);

      const withoutKey = stored.filter((entry) => entry.key !== scheduleKey);
      await persistStored([
        ...withoutKey,
        {
          key: scheduleKey,
          notificationId,
          tripId: briefing.tripId,
          contentDigest,
        },
      ]);
      recentSyncByTrip.set(briefing.tripId, { digest: syncDigest, at: Date.now() });
    }).catch((error) => {
      if (__DEV__) {
        console.warn('[useDayBriefingNotifications] Unable to schedule briefing', error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [briefing, syncDigest]);
}
